// Angular
import { Injectable } from '@angular/core';

// Libraries
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { CoreService } from '../services';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs';

// Services
import { LookupService } from '../services/lookup.service';
import { SpinnerService } from '../components/spinner/services/spinner.service';

// Models
import { AppCleanup, LookUpResponse } from '../models';

// Store
import { LookupActions } from './lookup.action';

// Enums
import { StatusType } from '../enums';

export interface LookupStateModel {
	loaded: boolean;
	item: LookUpResponse;
}

const defaultState: LookupStateModel = {
	loaded: false,
	item: undefined,
};

@State<any>({
	name: 'lookup',
	defaults: defaultState,
})
@Injectable()
export class LookupState extends AppCleanup {
	constructor(
		private spinnerService: SpinnerService,
		private translateService: TranslateService,
		private coreService: CoreService,
		private lookupService: LookupService
	) {
		super();
	}

	@Action(LookupActions.GetAll)
	getAll(ctx: StateContext<LookupStateModel>, action: LookupActions.GetAll) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.lookupService
			.getLookups()
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (lookups) => {
					let stateAbbreviations = lookups.lookUps.filter((lookup) => lookup.code === 'state');
					lookups.stateAbbreviations = stateAbbreviations;

					ctx.setState({
						item: lookups,
						loaded: true,
					});

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}
				},
				error: () => {
					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.ERROR_GETTING', { name: this.translateService.instant('MODULE.LOOKUP.TITLE_PLURAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(LookupActions.SetAll)
	setAll(ctx: StateContext<LookupStateModel>, action: LookupActions.SetAll) {
		ctx.setState({
			item: action.lookups,
			loaded: true,
		});
	}

	@Selector()
	static isLoaded(state: LookupStateModel): boolean {
		return state.loaded;
	}

	@Selector()
	static lookups(state: LookupStateModel): LookUpResponse {
		return state.item;
	}
}
